import { useMemo } from 'react';

import * as monthUtils from '@actual-app/core/shared/months';

import { parsePagosFijos } from '#components/mobile/budget/pagadas';
import { gastoPorCategoria } from '#components/mobile/informes/calculos';
import {
  analizarGastos,
  gastoPorCategoriaYMes,
  mesesComparacion,
  mesesPrevios,
} from '#components/mobile/informes/gastosRosa';
import { useCategoriasExcluidas } from '#components/mobile/informes/useCategoriasExcluidas';
import { useGastoHastaDia } from '#components/mobile/informes/useGastoHastaDia';
import { useTotalesMensuales } from '#components/mobile/informes/useTotalesMensuales';
import { useSyncedPref } from '#hooks/useSyncedPref';

/**
 * El gasto del mes en curso con lo habitual hasta hoy por categoría: el
 * mismo análisis que la página «En qué se va» con el periodo «Este mes».
 */
export function useGastoDelMes() {
  const mes = monthUtils.currentMonth();
  const dia = Number(monthUtils.currentDay().slice(8, 10));
  const diasMes = Number(monthUtils.lastDayOfMonth(mes).slice(8, 10));
  const { excluidas } = useCategoriasExcluidas();
  const { movimientos, categorias, meses, isLoading } = useTotalesMensuales({
    meses: 13,
    hasta: mes,
    categoriasExcluidas: excluidas,
  });
  const mesesPeriodo = useMemo(() => meses.slice(12), [meses]);
  const previos = useMemo(() => mesesPrevios(mesesPeriodo), [mesesPeriodo]);
  const hastaDia = useGastoHastaDia({
    meses: previos,
    dia,
    activo: true,
    categorias,
    excluidas,
  });
  const [rawFijos] = useSyncedPref('pagos-fijos');
  const fijosMarcados = useMemo(() => parsePagosFijos(rawFijos), [rawFijos]);

  const desglose = useMemo(
    () => gastoPorCategoria(movimientos, mesesPeriodo, categorias),
    [movimientos, mesesPeriodo, categorias],
  );
  const analisis = useMemo(
    () =>
      analizarGastos({
        filas: desglose.filas,
        meses: mesesPeriodo,
        comparacion: mesesComparacion({ tipo: 'mes', mes: null }, mesesPeriodo),
        previos,
        porMes: gastoPorCategoriaYMes(movimientos, categorias),
        hastaDia: hastaDia.datos ?? new Map(),
        fijosMarcados,
      }),
    [
      desglose,
      mesesPeriodo,
      previos,
      movimientos,
      categorias,
      hastaDia.datos,
      fijosMarcados,
    ],
  );

  return {
    mes,
    dia,
    diasMes,
    categorias,
    excluidas,
    desglose,
    analisis,
    isLoading: isLoading || hastaDia.isLoading,
  };
}
